import type { Cadence, Plan } from '../api';
import { PlanCard } from './PlanCard';

/** Sales inbox for the static "Custom" card's `mailto:` CTA — runtime copy, single place to edit
 * (never plan-API-driven, so it can't live in the `Plan` catalog). */
export const CONTACT_SALES_MAILTO = 'sales@optimusjournal.com';

/**
 * The Custom card as a `Plan`, so it can go through the SAME renderer as the API-driven ones.
 * `tier: 'pro'` is inert here — nothing reads it, because this card is never matched against the
 * caller's subscription (see `isCurrent={false}` below) — but the type requires one of the two.
 */
const CUSTOM_PLAN: Plan = {
  tier: 'pro',
  name: 'Custom',
  description: 'For desks and teams',
  features: [
    'Everything in Pro, for your whole desk',
    'Dedicated onboarding',
    'Priority support',
  ],
  monthlyPriceCents: 0,
  annualPriceCents: 0,
  currency: 'usd',
  aiCreditsPerMonth: 0,
  isFeatured: false,
  isPurchasable: false,
};

/**
 * Static third card in the pricing row ("Custom / Let's talk") — never plan-API-driven.
 *
 * **It RENDERS `PlanCard`. It no longer re-implements it.** This file used to carry its own copy of
 * the two-panel shell, with a comment claiming it "mirrors PlanCard … so the row reads as one
 * consistent set of three cards". Mirroring by duplication is exactly how it stopped matching: every
 * Figma correction made to `PlanCard` — the 411:395 flex ratio, `drop-shadow` instead of
 * `shadow-card`, the 42px name/price/CTA rhythm, 16px feature gaps, the hairline borders, and the
 * `flex-1` bottom panel that lets a card fill its stretched height — landed on one component and
 * silently skipped this one, so Custom rendered visibly narrower, flatter and shorter than its
 * neighbours (2026-08-16 user report: "the plans components are not equal in size").
 *
 * Delegating means the row cannot drift again: there is one shell, and this card differs only in the
 * two ways it genuinely is different — a price that is words rather than a number, and a CTA that
 * leaves the app.
 */
export function ContactSalesCard({ cadence }: { cadence: Cadence }) {
  return (
    <PlanCard
      plan={CUSTOM_PLAN}
      cadence={cadence}
      priceLabel={"Let’s talk"}
      // Never "your plan": there is no Custom subscription to be on — it is a conversation.
      isCurrent={false}
      cta={{ label: 'Contact sales', disabled: false, href: `mailto:${CONTACT_SALES_MAILTO}` }}
    />
  );
}
